<template>
  <nav aria-label="Main navigation">
    <div v-if="showDeliveryNotification && userHasOrderBundleAffectedByDelay && !hideDeliveryNotificationOnThisRoute" class="bg-yellow-400 py-2 px-4 md:py-rem">
      <div class="new-navbar-notification-container flex items-center">
        <OSvg svg="info" class="fill-black w-5 h-5 mr-rem flex-shrink-0" />
        <div>
          <span class="o-caption font-bold mr-1" v-text="$t('Notification.TopBanner.Bold', extraVariables)" />
          <span class="o-caption" v-text="$t('Notification.TopBanner.Normal', extraVariables)" />
        </div>
        <button class="appearance-none focus:outline-none cursor-pointer ml-auto pl-5 flex-shrink-0" @click="closeDeliveryNotification">
          <OSvg svg="cross" class="fill-black w-3 h-3" />
        </button>
      </div>
    </div>
    <div v-else-if="showEmailMigrationNotification && shouldShowEmailMigrationNotification" class="bg-yellow-400 py-2 px-4 md:py-rem">
      <div class="new-navbar-notification-container flex items-center">
        <OSvg svg="info" class="fill-black w-5 h-5 mr-rem flex-shrink-0" />
        <div>
          <span class="o-caption font-bold mr-1" v-text="'Today between 8:00 PM and 9:00 PM (CET), we will be performing technical maintenance.'" />
          <span class="o-caption" v-text="'Due to limited email availability, we kindly ask you to send your email after 9:00 PM.'" />
        </div>
        <button class="appearance-none focus:outline-none cursor-pointer ml-auto pl-5 flex-shrink-0" @click="closeEmailMigrationNotification">
          <OSvg svg="cross" class="fill-black w-3 h-3" />
        </button>
      </div>
    </div>
    <div v-if="showCategorySwitch" class="bg-neutral-1000">
      <div class="new-navbar-container bg-neutral-1000">
        <div class="category-switcher flex bg-neutral-00" role="tablist" aria-label="Category selection">
          <div class="category-switcher-padding w-3 bg-neutral-1000" :class="{ 'men-active': isMen, 'women-active': !isMen }" />
          <div class="category-switcher-men" :class="{ 'men-active': isMen, 'women-active': !isMen }">
            <NuxtLink
              to="/men"
              :aria-label="$t('Accessibilty.AriaLabels.GoToMenHomepage')"
              class="o-footer py-1 px-4 inline-block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              role="tab"
              :aria-selected="isMen"
              tabindex="0"
            >
              {{ $t('ChooseCategoryPage.Men') }}
            </NuxtLink>
          </div>
          <div class="category-switcher-women" :class="{ 'men-active': isMen, 'women-active': !isMen }">
            <NuxtLink
              to="/women"
              :aria-label="$t('Accessibilty.AriaLabels.GoToWomenHomepage')"
              class="o-footer py-1 px-4 inline-block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              role="tab"
              :aria-selected="isWomen"
              tabindex="0"
            >
              {{ $t('ChooseCategoryPage.Women') }}
            </NuxtLink>
          </div>
          <div class="category-switcher-grow bg-neutral-1000 flex-grow border-b" :class="{ 'men-active': isMen, 'women-active': !isMen }">
            <div />
          </div>
        </div>
      </div>
    </div>
    <div
      class="new-navbar px-4 border-b border-neutral-200"
      :class="{
        'py-4 py-even-more': showUserIcon,
        'py-5': !showUserIcon && !specialDesign,
        'py-2': specialDesign,
        'py-even-more-reduce': isStandard
      }"
    >
      <div class="new-navbar-container flex relative">
        <button
          v-if="showHamburger"
          v-click-outside="onMobileMenuClickOutside"
          class="flex items-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 mr-3 lg:hidden"
          :aria-label="$t('Accessibilty.AriaLabels.OpenMenu')"
          @click="openMobileMenu"
          @keydown.enter="openMobileMenu"
          @keydown.space.prevent="openMobileMenu"
        >
          <OSvg svg="hamburger" class="w-6 h-auto text-neutral-800 fill-current" />
        </button>
        <button
          v-if="isAccount"
          class="flex items-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 mr-3 lg:hidden"
          :aria-label="$t('Accessibilty.AriaLabels.OpenCountryLanguageMenu')"
          @click="onCountryLanguageClick"
          @keydown.enter="onCountryLanguageClick"
          @keydown.space.prevent="onCountryLanguageClick"
        >
          <OSvg :svg="country" class="w-7 h-auto rounded" />
        </button>
        <div class="flex-grow flex justify-center items-center" :class="{ 'lg:justify-start': !isSignup, 'lg:flex-grow-0 mr-auto': isStandard || isStandardLoggedIn }">
          <NuxtLink
            v-if="!specialDesign"
            :to="isAccount ? '/account' : '/'"
            :aria-label="isAccount ? $t('Accessibilty.AriaLabels.GoToAccount') : $t('Accessibilty.AriaLabels.GoToHomepage')"
            class="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <OSvg svg="logo" class="logo-width max-w-full text-black fill-current" />
          </NuxtLink>
          <OImage v-else v-bind="specialDesignLogo" img-class="special-design-logo-width pointer-events-none max-w-full" />
        </div>
        <div v-if="isStandard || isStandardLoggedIn" class="hidden flex-grow lg:flex justify-center items-center">
          <div class="inline-flex mx-auto gap-9" role="menubar" aria-label="Main menu">
            <NuxtLink
              v-for="(menuItem) in menuItems"
              :key="menuItem.link"
              v-t="menuItem.text"
              :to="{ path: $i18n.localePath({ name: menuItem.nuxtLink, params: { ...(menuItem.params ? menuItem.params : {}) } }), hash: menuItem.hash }"
              class="relative block text-black whitespace-no-wrap o-body focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              :tabindex="'0'"
              role="menuitem"
              @click.native="onMenuClick(menuItem)"
              @keydown.enter="onMenuClick(menuItem)"
              @keydown.space.prevent="onMenuClick(menuItem)"
            />
          </div>
        </div>
        <div v-if="showUserIcon" class="focus:outline-none ml-3 flex items-center">
          <button
            v-if="isAccount || isStandardLoggedIn || isStandard"
            class="items-center hidden focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 mr-10 lg:flex"
            :class="{ 'lg:mr-6': isStandard }"
            :aria-label="$t('Accessibilty.AriaLabels.OpenCountryLanguageMenu')"
            @click="onCountryLanguageClick"
            @keydown.enter="onCountryLanguageClick"
            @keydown.space.prevent="onCountryLanguageClick"
          >
            <OSvg :svg="country" class="w-7 h-auto rounded" />
          </button>
          <button
            v-click-outside="onClickOutside"
            class="relative flex focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            :aria-label="$t('Accessibilty.AriaLabels.OpenCountryLanguageMenu')"
            :class="{ 'lg:hidden': isStandard }"
            @click="onUserMenuClick"
            @keydown.enter="onUserMenuClick"
            @keydown.space.prevent="onUserMenuClick"
          >
            <OSvg svg="user" class="w-6 text-neutral-800 fill-current" :class="{ 'lg:hidden': isStandard }" />
            <span class="o-body ml-2 hidden lg:block" :class="{ 'lg:hidden': isStandard }" v-text="$t('Navigation.Account')" />
          </button>
        </div>
        <div v-if="isStandard" class="hidden lg:flex">
          <div class="mr-4">
            <NewButton
              color="white"
              variant="outline"
              tag="nuxt-link"
              to="/login"
              icon-right="new-chevron-right"
              class="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {{ $t('LoginForm.Login') }}
            </NewButton>
          </div>
          <div>
            <NewButton
              tag="nuxt-link"
              to="/signup"
              icon-right="new-chevron-right"
              class="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {{ $t('HomePage.Signup') }}
            </NewButton>
          </div>
        </div>
        <UserMenu
          v-bind="userMenuProps"
          @logout="logout"
        />
      </div>
      <CountryLocaleModal v-bind="countryLocaleModalProps" />
      <MobileMenu
        v-bind="mobileMenuProps"
        @locale="onCountryLanguageClick"
      />
    </div>
  </nav>
</template>

<script>
import OSvg from '@/components/OSvg'
import CountryLocaleModal from '@/components/NewNavbar/CountryLocaleModal'
import UserMenu from '@/components/NewNavbar/UserMenu'
import MobileMenu from '@/components/NewNavbar/MobileMenu'
import NewButton from '@/components/NewButton'
import OImage from '@/components/OImage'
import { AUTH_USER, IS_AUTHENTICATED } from '@/utils/store/getterTypes'
import { NavVariant } from '@/enums/nav'
import { mapState, mapGetters } from 'vuex'
import { WebsiteCategories } from '@/enums/app'
import { Orientations } from '@/enums/products'
import { ROUTER_HASH_UPDATED } from '@/events/eventTypes'
import { CountryCodes, CountrySelectOptionsLimited, WomenCountries } from '@/enums/general'
import { getImagePaths } from '@/utils/general'
import { CDELIVERYNOTIFICATION, CEMAILMIGRATIONNOTIFICATION } from '@/utils/cookie/cookieTypes'
import { getCookieDomain } from '@/utils/cookie'
import { PaymentStatuses } from '@/enums/payments'

export default {
  name: 'NewNavbar',
  components: { OSvg, NewButton, CountryLocaleModal, UserMenu, MobileMenu, OImage },
  props: {
    variant: {
      type: String,
      default: NavVariant.signup
    }
  },
  data () {
    return {
      showDeliveryNotification: !this.$cookies.get(CDELIVERYNOTIFICATION),
      showEmailMigrationNotification: !this.$cookies.get(CEMAILMIGRATIONNOTIFICATION),
      extraVariables: {
        delayedDeliveryDateEarliest: null,
        delayedDeliveryDateLatest: null,
        delayedDesign: null
      }
    }
  },
  computed: {
    isSignup () {
      return this.variant === NavVariant.signup
    },
    isAccount () {
      return this.variant === NavVariant.account
    },
    isStandard () {
      return this.variant === NavVariant.standard && !this.isStandardLoggedIn
    },
    isStandardLoggedIn () {
      return this.variant === NavVariant.standard && this.isAuthenticated
    },
    hideCategorySwitchOnThisRoute () {
      const routes = [
        'lang',
        'lang-login',
        'lang-affiliate',
        'lang-try-now',
        'lang-undershirts',
        'lang-reactivate',
        'lang-password-reset',
        'lang-password-reset-token'
      ]

      return routes.includes(this.$route.name)
    },
    hideDeliveryNotificationOnThisRoute () {
      const routes = [
        'lang-men-signup',
        'lang-women-signup'
      ]

      return routes.includes(this.$route.name)
    },
    showCategorySwitch () {
      return this.isWomenCountry && !this.hideCategorySwitchOnThisRoute && (this.isStandard || this.isStandardLoggedIn) && !this.specialDesign
    },
    specialDesign () {
      const isSpecialCountdownPage = this.specialDesigns.find(design => design.url === this.$route.params.specialDesignUrl)

      if (isSpecialCountdownPage) {
        return isSpecialCountdownPage
      }

      const isSpecialOnepager = this.specialDesigns.find(design => design.slug === this.$route.params.design)
      const isSpecialSelectedOnTheOnepager = this.form.fixedDesign.slug === this.$route.params.design

      if (isSpecialOnepager && isSpecialSelectedOnTheOnepager) {
        return isSpecialOnepager
      }

      return false
    },
    specialDesignLogo () {
      return getImagePaths({
        s3: true,
        noMobile: true,
        path: `/images/design/${this.specialDesign?.slug}/logo-header`
      })
    },
    isWomenCountry () {
      return WomenCountries.includes(this.country)
    },
    showUserIcon () {
      return this.isAccount || this.isStandard || this.isStandardLoggedIn
    },
    showHamburger () {
      return this.isStandard || this.isStandardLoggedIn
    },
    currentCategory () {
      const withoutLangParam = this.$route.name?.replace('lang-', '')

      if ((withoutLangParam ?? '').startsWith(WebsiteCategories.women)) {
        return WebsiteCategories.women
      }

      return WebsiteCategories.men
    },
    isMen () {
      return this.currentCategory === WebsiteCategories.men
    },
    isWomen () {
      return this.currentCategory === WebsiteCategories.women
    },
    menuItems () {
      return [
        {
          text: 'Navigation.HowDoesItWork',
          nuxtLink: this.currentCategory,
          hash: '#how-it-works'
        },
        ...(this.currentCategory === WebsiteCategories.men ? [{
          text: 'Navigation.Lookbook',
          nuxtLink: 'lookbook-orientation',
          params: {
            orientation: Orientations.menoriginals
          }
        }] : []),
        ...(this.currentCategory === WebsiteCategories.women ? [{
          text: 'Navigation.Lookbook',
          nuxtLink: 'women-product'
        }] : []),
        {
          text: 'Navigation.FAQ',
          nuxtLink: 'customer-service'
        }
      ]
    },
    countryOptions () {
      if (this.isWomen) {
        return CountrySelectOptionsLimited.map(option => ({ ...option, label: this.$t(option.label) }))
      }

      return Object.keys(CountryCodes).map(key => ({
        svg: CountryCodes[key],
        label: this.$t(`Countries.${CountryCodes[key]}`),
        value: CountryCodes[key]
      }))
    },
    countryLocaleModalProps () {
      return {
        ref: 'countryLanguage',
        country: this.country,
        locale: this.locale,
        disableCountry: this.isAuthenticated,
        countryOptions: this.countryOptions
      }
    },
    userMenuProps () {
      return {
        ref: 'userMenu',
        isLoggedIn: this.isAuthenticated,
        name: this.userFirstName,
        email: this.userEmail
      }
    },
    shouldShowEmailMigrationNotification () {
      const now = this.$moment.utc()

      const maintenanceStartTime = this.$moment.utc('09-11-2025 19:00', 'DD-MM-YYYY HH:mm')
      const maintenanceEndTime = this.$moment.utc('09-11-2025 20:00', 'DD-MM-YYYY HH:mm')

      const routes = [
        'lang-men-customer-service',
        'lang-women-customer-service'
      ]

      return now.isAfter(maintenanceStartTime) && now.isBefore(maintenanceEndTime) && routes.includes(this.$route.name)
    },
    userHasOrderBundleAffectedByDelay () {
      if (this.extraVariables.delayedDeliveryDateEarliest) {
        return true
      }

      return false
    },
    mobileMenuProps () {
      return {
        ref: 'mobileMenu',
        locale: this.locale
      }
    },
    ...mapState('app', {
      country: state => state.country,
      locale: state => state.locale
    }),
    ...mapState('auth', {
      userEmail: state => state.user?.user?.email,
      userFirstName: state => state.user?.user?.firstName
    }),
    ...mapState('specialDesigns', {
      specialDesigns: state => state.specialDesigns
    }),
    ...mapGetters('auth', {
      isAuthenticated: IS_AUTHENTICATED,
      authUser: AUTH_USER
    }),
    ...mapState('signup', {
      form: state => state.form
    })
  },
  mounted () {
    const now = this.$moment.utc()

    const edges = this.authUser?.user?.orderBundles?.edges || []

    edges.find((edge) => {
      const paymentIsPaid = edge.node.payment.status === PaymentStatuses.paid
      // Find delayed delivery in orders
      const delayedOrder = edge.node.orders.edges.find((orderEdge) => {
        const order = orderEdge.node
        const delayedDeliveryDate = order.delayedDeliveryDate

        if (!delayedDeliveryDate) {
          return false
        }

        const delayedDeliveryDateIsAfterNormalDeliveryDate = this.$moment.utc(delayedDeliveryDate).isAfter(this.$moment.utc(order.shipment.deliveryDate * 1000))
        const nowIsBeforeDelayedDeliveryDate = now.isBefore(this.$moment.utc(delayedDeliveryDate).add(3, 'day'))
        const isOrderDelayed = paymentIsPaid && nowIsBeforeDelayedDeliveryDate && delayedDeliveryDateIsAfterNormalDeliveryDate

        if (isOrderDelayed) {
          this.extraVariables.delayedDeliveryDateEarliest = this.formatDate({ date: this.$moment.utc(delayedDeliveryDate).subtract(3, 'day') })
          this.extraVariables.delayedDeliveryDateLatest = this.formatDate({ date: this.$moment.utc(delayedDeliveryDate).add(3, 'day') })
          this.extraVariables.delayedDesign = order.product.design.name
        }

        return isOrderDelayed
      })

      return delayedOrder !== undefined
    })
  },
  methods: {
    formatDate ({ date }) {
      return `${date.date()} ${this.$t(`Months[${date.month()}]`)}`
    },
    closeEmailMigrationNotification () {
      this.showEmailMigrationNotification = false

      this.$cookies.set(CEMAILMIGRATIONNOTIFICATION, true, {
        path: '/',
        maxAge: 60 * 60 * 3,
        domain: getCookieDomain()
      })
    },
    closeDeliveryNotification () {
      this.showDeliveryNotification = false

      this.$cookies.set(CDELIVERYNOTIFICATION, true, {
        path: '/',
        maxAge: 60 * 60 * 3,
        domain: getCookieDomain()
      })
    },
    onMenuClick (menuItem) {
      if (menuItem.hash) {
        this.$fireEvent(ROUTER_HASH_UPDATED.event, menuItem.hash)
      }
    },
    openMobileMenu () {
      this.$refs?.mobileMenu?.togglePopover(true)
    },
    onUserMenuClick () {
      this.$refs?.userMenu?.togglePopover(true)
    },
    onCountryLanguageClick () {
      this.$refs?.countryLanguage?.toggleActivation(true)
    },
    onClickOutside () {
      this.$refs?.userMenu?.onClickOutside()
    },
    onMobileMenuClickOutside () {
      this.$refs?.mobileMenu?.onClickOutside()
    },
    logout () {
      this.$auth.logout()
    }
  }
}
</script>

<style scoped lang="postcss">
.logo-width {
  width: 14.6rem;
}

.new-navbar-notification-container {
  @apply mx-auto;
  max-width: 119.2rem;
}

.category-switcher {
  &-padding {
    &.men-active {
      border-bottom-right-radius: .4rem;
    }
  }

  &-men {
    &.men-active {
      @apply bg-neutral-1000;

      a {
        @apply bg-neutral-00;
        border-top-left-radius: .4rem;
        border-top-right-radius: .4rem;
      }
    }

    &.women-active {
      @apply bg-neutral-00;

      a {
        @apply bg-neutral-1000 text-neutral-00;
        border-bottom-right-radius: .4rem;
      }
    }
  }

  &-women {
    &.men-active {
      @apply bg-neutral-1000 text-neutral-00;
      border-bottom-left-radius: .4rem;
    }

    &.women-active {
      @apply bg-neutral-1000;

      a {
        @apply bg-neutral-00 text-neutral-800;
        border-top-left-radius: .4rem;
        border-top-right-radius: .4rem;
      }
    }
  }

  &-grow {
    @apply border-none;

    &.men-active {
      @apply bg-neutral-1000;
    }

    &.women-active {
      @apply bg-neutral-00;

      div {
        @apply bg-neutral-1000 w-full h-full;
        border-bottom-left-radius: .4rem;
      }
    }
  }
}

.new-navbar-container {
  @apply mx-auto;
  max-width: 119.2rem;
}

@screen lg {
  .logo-width {
    width: 20rem;
  }

  .py-even-more {
    padding-top: 3.8rem;
    padding-bottom: 3.8rem;

    &.py-even-more-reduce {
      @apply py-7;
    }
  }
}

/* Add focus styles */
.focus\:ring-primary:focus {
  --ring-color: #36C0B6;
  --ring-offset-width: 2px;
  --ring-offset-color: #fff;
  --ring-width: 2px;
  box-shadow: 0 0 0 var(--ring-offset-width) var(--ring-offset-color), 0 0 0 calc(var(--ring-offset-width) + var(--ring-width)) var(--ring-color);
}

.focus\:ring-offset-2:focus {
  --ring-offset-width: 2px;
}
</style>
